import { useEffect, useRef } from "react";

// Pure-white background with a blue particle network.
// Each node has depth (z): far nodes are small & light blue, and as they move
// toward the screen they grow and deepen to a darker blue.
const LIGHT = [124, 196, 255]; // #7CC4FF (far)
const DEEP = [11, 71, 184]; //   #0B47B8 (near)

const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const colorAt = (t) => `rgb(${lerp(LIGHT[0], DEEP[0], t)}, ${lerp(LIGHT[1], DEEP[1], t)}, ${lerp(LIGHT[2], DEEP[2], t)})`;

export const NetworkBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let nodes = [];
    let raf;
    const mouse = { x: -9999, y: -9999, active: false };

    const count = () => {
      const base = Math.round((window.innerWidth * window.innerHeight) / 22000);
      return Math.max(45, Math.min(110, base));
    };

    const spawn = () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      z: Math.random(), // depth 0 (far) -> 1 (near)
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      vz: 0.0009 + Math.random() * 0.0016, // drift toward the screen
    });

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodes = Array.from({ length: count() }, spawn);
    };

    const CONNECT = 150;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // connections
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECT) {
            const t = (a.z + b.z) / 2;
            const alpha = (1 - dist / CONNECT) * (0.10 + t * 0.28);
            ctx.strokeStyle = `rgba(${lerp(LIGHT[0], DEEP[0], t)}, ${lerp(LIGHT[1], DEEP[1], t)}, ${lerp(LIGHT[2], DEEP[2], t)}, ${alpha})`;
            ctx.lineWidth = 0.6 + t * 0.9;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // nodes
      for (const n of nodes) {
        const r = 1 + n.z * 3.2;
        ctx.beginPath();
        ctx.fillStyle = colorAt(n.z);
        ctx.globalAlpha = 0.35 + n.z * 0.6;
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // cursor interaction: link nearby nodes to the pointer
      if (mouse.active) {
        const R = 210;
        for (const n of nodes) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < R) {
            const alpha = (1 - dist / R) * 0.55;
            ctx.strokeStyle = `rgba(11, 71, 184, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.fillStyle = "rgba(20, 102, 214, 0.9)";
        ctx.arc(mouse.x, mouse.y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = () => {
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        n.z += n.vz;
        // gently push away from the cursor for a reactive feel
        if (mouse.active) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const dist = Math.hypot(dx, dy) || 1;
          const R = 150;
          if (dist < R) {
            const f = (1 - dist / R) * 1.4;
            n.x += (dx / dist) * f;
            n.y += (dy / dist) * f;
          }
        }
        if (n.z > 1) {
          // recycle: reappears far away at a new position
          n.z = 0;
          n.x = Math.random() * w;
          n.y = Math.random() * h;
        }
        if (n.x < -20) n.x = w + 20;
        else if (n.x > w + 20) n.x = -20;
        if (n.y < -20) n.y = h + 20;
        else if (n.y > h + 20) n.y = -20;
      }
      draw();
      raf = requestAnimationFrame(step);
    };

    resize();
    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onTouch = (e) => {
      if (e.touches && e.touches[0]) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
        mouse.active = true;
      }
    };
    const onLeave = () => {
      mouse.active = false;
    };

    if (reduce) {
      draw();
    } else {
      window.addEventListener("mousemove", onMove, { passive: true });
      window.addEventListener("touchmove", onTouch, { passive: true });
      window.addEventListener("mouseout", onLeave);
      raf = requestAnimationFrame(step);
    }
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div aria-hidden="true" className="fixed inset-0 z-0 bg-white pointer-events-none" data-testid="network-bg">
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
};
